import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { ACTIVITY_MARKER_CENTER_Y } from '../config/purchase-order-activity-config';
import {
  getActivityAccentClassName,
  getActivityIcon,
  getActivityMarkerSize,
} from '../helpers/purchase-order-activity-appearance';
import {
  formatLocalizedDateTime,
  getActorLabel,
} from '../helpers/purchase-order-activity-text';
import type {
  PurchaseOrderActivityDetailState,
  PurchaseOrderActivityItem,
} from '../types/purchase-order-activity-item';
import { renderActivityContent } from './purchase-order-activity-render-content';

type PurchaseOrderActivityCardProps = {
  activity: PurchaseOrderActivityItem;
  documentUrlByName: Record<string, string>;
  isLast: boolean;
  locale: string;
  onOpenDetail: (detail: PurchaseOrderActivityDetailState) => void;
};

export function PurchaseOrderActivityCard({
  activity,
  documentUrlByName,
  isLast,
  locale,
  onOpenDetail,
}: PurchaseOrderActivityCardProps) {
  const { t } = useTranslation('purchaseOrder');
  const actorLabel = getActorLabel(activity, t);
  const markerSize = getActivityMarkerSize(activity);
  const markerStyle: CSSProperties = {
    height: `${markerSize}px`,
    top: `${ACTIVITY_MARKER_CENTER_Y - markerSize / 2}px`,
    width: `${markerSize}px`,
  };

  return (
    <div className='relative pl-16'>
      {isLast ? null : (
        <div className='absolute top-3 bottom-[-2rem] left-6 z-0 w-px -translate-x-1/2 bg-border' />
      )}
      <div
        className={cn(
          'absolute left-6 z-10 flex -translate-x-1/2 items-center justify-center rounded-full border',
          getActivityAccentClassName(activity),
        )}
        style={markerStyle}
      >
        {getActivityIcon(activity, markerSize)}
      </div>
      <div className='grid gap-4 md:grid-cols-[minmax(0,1fr)_160px] md:items-start md:gap-7'>
        <div className='min-w-0'>
          <div className='flex flex-wrap items-center gap-x-3 gap-y-2 text-base leading-7'>
            {renderActivityContent({
              activity,
              documentUrlByName,
              locale,
              t,
              onOpenDetail,
            })}
            <span className='text-muted-foreground'>
              {t('by', { ns: 'common' })} {actorLabel}
            </span>
          </div>
        </div>
        <span className='shrink-0 whitespace-nowrap text-sm text-zinc-400 md:justify-self-end'>
          {formatLocalizedDateTime(activity.recordedAt, locale)}
        </span>
      </div>
    </div>
  );
}
